<template>
  <v-dialog
    :model-value="modelValue"
    max-width="920"
    scrollable
    persistent
    class="budget-wizard-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="budget-wizard">
      <header class="budget-wizard__head">
        <div class="budget-wizard__tools">
          <button v-if="showSheetLink" type="button" class="budget-wizard__tool" @click="requestSheet">표로 되돌리기</button>
          <button type="button" class="budget-wizard__clear" @click="askKind = 'reset'">모두 지우기</button>
          <button type="button" class="budget-wizard__save" :disabled="viewSource === 'draft' && !dirty" @click="saveDraft()">임시저장</button>
          <button type="button" class="dialog-close-x" title="닫기" @click="requestClose">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="budget-wizard__meta">
          <span>{{ year }} {{ budgetLabel }}</span>
          <span>{{ bizGubun || "사업" }}</span>
        </div>
        <div class="budget-wizard__title-row">
          <h2 class="budget-wizard__title">{{ step.title }}</h2>
          <span class="budget-wizard__count">{{ stepIndex + 1 }}/{{ steps.length }}</span>
        </div>
      </header>

      <nav class="budget-wizard__nav" aria-label="단계">
        <template v-for="(item, index) in steps" :key="item.id">
          <button
            type="button"
            class="budget-wizard__nav-btn"
            :class="{ 'is-on': index === stepIndex, 'is-done': index < stepIndex }"
            @click="goTo(index)"
          >
            <span class="budget-wizard__nav-num">
              <v-icon v-if="index < stepIndex" size="11">mdi-check</v-icon>
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span class="budget-wizard__nav-label">{{ item.nav }}</span>
          </button>
          <span v-if="index < steps.length - 1" class="budget-wizard__nav-sep"></span>
        </template>
      </nav>

      <div class="budget-wizard__body" @keydown.enter="focusNextField">
        <section v-if="step.id === 'people'">
          <p v-if="!sugaList.length" class="budget-wizard__warn">수가 없음</p>
          <template v-else>
            <div class="wizard-grid" :class="peopleGridClass">
              <div class="wizard-grid__head">
                <span>등급</span>
                <span v-if="family === 'care'">시간대</span>
                <span v-else-if="family === 'bath'">차량 사용</span>
                <span v-else-if="family === 'day'">시간대</span>
                <template v-if="usesBasic">
                  <span class="hc">일반</span>
                  <span class="hc">기초수급</span>
                </template>
                <span v-else class="hc">인원</span>
                <span class="hc">월 횟수</span>
                <span class="hr">연간</span>
                <span></span>
              </div>
              <div v-for="(grade, index) in answers.grades" :key="grade.id" class="wizard-grid__row">
                <select v-if="family === 'day' || family === 'facility'" v-model="grade.label" class="budget-wizard__select">
                  <option v-for="name in gradeLabelOptions(bizGubun)" :key="name" :value="name">{{ name }}</option>
                </select>
                <span v-else class="wizard-grid__grade">{{ grade.label }}</span>
                <select v-if="family === 'care' || family === 'bath'" v-model="grade.rank" class="budget-wizard__select">
                  <option v-for="suga in sugaList" :key="suga.suga_rank" :value="suga.suga_rank">{{ suga.suga_rank }}</option>
                </select>
                <select v-else-if="family === 'day'" v-model="grade.hours" class="budget-wizard__select">
                  <option v-for="hour in hourOptions" :key="hour" :value="hour">{{ hour }}</option>
                </select>
                <input class="budget-wizard__input budget-wizard__input--c" inputmode="numeric" :value="grade.count" placeholder="0" @input="onDigits(grade, 'count', $event)" />
                <input v-if="usesBasic" class="budget-wizard__input budget-wizard__input--c" inputmode="numeric" :value="grade.basicCount" placeholder="0" @input="onDigits(grade, 'basicCount', $event)" />
                <input class="budget-wizard__input budget-wizard__input--c" inputmode="numeric" :value="grade.times" placeholder="0" @input="onDigits(grade, 'times', $event)" />
                <div class="wizard-grid__figures">
                  <template v-if="pairPreview(grade)">
                    <span v-if="pairPreview(grade)!.showNormal"><em>본인부담</em>{{ formatWon(pairPreview(grade)!.bonin) }}</span>
                    <span v-if="pairPreview(grade)!.showNormal"><em>장기요양급여</em>{{ formatWon(pairPreview(grade)!.benefit) }}</span>
                    <span v-if="pairPreview(grade)!.showBasic"><em>기초수급</em>{{ formatWon(pairPreview(grade)!.basicBenefit) }}</span>
                  </template>
                </div>
                <button v-if="answers.grades.length > 1" type="button" class="wizard-grid__del" title="행 삭제" @click="removeGrade(index)">
                  <v-icon size="15">mdi-close</v-icon>
                </button>
                <span v-else></span>
              </div>
            </div>
            <button type="button" class="budget-wizard__ghost budget-wizard__ghost--block" @click="addGrade">
              <v-icon size="15">mdi-plus</v-icon>
              추가
            </button>
          </template>
        </section>

        <section v-else-if="step.id === 'staff'">
          <div class="wizard-grid wizard-grid--staff">
            <div class="wizard-grid__head">
              <span>직종</span>
              <span class="hc">인원</span>
              <span class="hr">월급</span>
              <span class="hc">대표</span>
              <span class="hr">연간</span>
              <span></span>
            </div>
            <div v-for="(person, index) in answers.staff" :key="person.id" class="wizard-grid__row">
              <select v-model="person.role" class="budget-wizard__select" @change="onStaffRole(person)">
                <option v-if="person.role && !salaryRoleOptions(person.kind, bizGubun).includes(person.role)" :value="person.role">{{ person.role }}</option>
                <option v-for="role in salaryRoleOptions(person.kind, bizGubun)" :key="role" :value="role">{{ role }}</option>
              </select>
              <input class="budget-wizard__input budget-wizard__input--c" inputmode="numeric" :value="person.count" placeholder="0" @input="onDigits(person, 'count', $event)" />
              <input class="budget-wizard__input budget-wizard__input--r" inputmode="numeric" :value="person.monthly" placeholder="0" @input="onWon(person, 'monthly', $event)" />
              <label v-if="person.role === '센터장'" class="wizard-grid__check" title="대표">
                <input v-model="person.isCeo" type="checkbox" />
              </label>
              <span v-else></span>
              <div class="wizard-grid__figures">
                <span v-if="staffPreview(person)">{{ formatWon(staffPreview(person)!.annual) }}</span>
              </div>
              <button type="button" class="wizard-grid__del" title="행 삭제" @click="removeStaff(index)">
                <v-icon size="15">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div class="budget-wizard__add-row">
            <button type="button" class="budget-wizard__ghost" @click="addStaff('direct')">
              <v-icon size="15">mdi-plus</v-icon>
              현장 추가
            </button>
            <button type="button" class="budget-wizard__ghost" @click="addStaff('indirect')">
              <v-icon size="15">mdi-plus</v-icon>
              센터장 추가
            </button>
          </div>
          <div v-if="familyUsesTreatment(family)" class="wizard-mini">
            <h3 class="wizard-mini__title">처우개선비</h3>
            <div class="wizard-mini__row">
              <label class="budget-wizard__field">
                <span>인원</span>
                <input class="budget-wizard__input budget-wizard__input--c" inputmode="numeric" :value="answers.treatmentCount" placeholder="0" @input="onAnswerDigits('treatmentCount', $event)" />
              </label>
              <label class="budget-wizard__field budget-wizard__field--wide">
                <span>기타보조금 (한 달)</span>
                <input class="budget-wizard__input budget-wizard__input--r" inputmode="numeric" :value="answers.treatmentSubsidy" placeholder="0" @input="onAnswerWon('treatmentSubsidy', $event)" />
              </label>
              <label class="budget-wizard__field budget-wizard__field--wide">
                <span>본인부담 (한 달)</span>
                <input class="budget-wizard__input budget-wizard__input--r" inputmode="numeric" :value="answers.treatmentCopay" placeholder="0" @input="onAnswerWon('treatmentCopay', $event)" />
              </label>
              <div class="wizard-grid__figures">
                <span v-if="treatmentAnnual() > 0">{{ formatWon(treatmentAnnual()) }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-else-if="step.id === 'income' || step.id === 'spend'">
          <label class="wizard-find">
            <input v-model="moneyQuery" class="budget-wizard__input wizard-find__input" placeholder="항목 이름 찾기" />
          </label>
          <section v-for="gwan in visibleMoneyGroups" :key="gwan.gwan" class="wizard-gwan">
            <h3 class="wizard-gwan__name">{{ gwan.gwan }}</h3>
            <div v-for="hang in gwan.hangs" :key="gwan.gwan + '-' + hang.hang" class="wizard-hang" :class="{ 'is-plain': !hang.showHang }">
              <h4 v-if="hang.showHang" class="wizard-hang__name">{{ hang.hang }}</h4>
              <section v-for="group in hang.groups" :key="gwan.gwan + '-' + hang.hang + '-' + group.title" class="wizard-money">
                <h5 v-if="group.fields.length > 1" class="wizard-money__title">{{ group.title }}</h5>
            <div v-for="field in group.fields" :key="field.id" class="wizard-money__row">
              <div class="wizard-money__name">
                <strong>{{ field.label }}</strong>
              </div>
              <span class="wizard-money__tag">{{ moneyInputCopy(field.mode).a }}</span>
              <input
                class="budget-wizard__input budget-wizard__input--r"
                inputmode="numeric"
                :value="moneyBag(field.id).a"
                placeholder="0"
                @input="onMoney(field.id, 'a', $event)"
              />
              <span class="wizard-money__tag wizard-money__tag--b">{{ moneyInputCopy(field.mode).b }}</span>
              <input
                v-if="moneyInputCopy(field.mode).b"
                class="budget-wizard__input budget-wizard__input--c"
                inputmode="numeric"
                :value="moneyBag(field.id).b"
                :placeholder="field.mode === 'span' ? '12' : '0'"
                @input="onMoney(field.id, 'b', $event)"
              />
              <span v-else class="wizard-money__slot"></span>
              <span class="wizard-money__year" :class="{ 'is-on': annualFieldAmount(field) > 0 }">
                {{ annualFieldAmount(field) > 0 ? "1년 " + formatWon(annualFieldAmount(field)) + "원" : "" }}
              </span>
            </div>
              </section>
            </div>
          </section>
          <p v-if="!visibleMoneyGroups.length" class="budget-wizard__quiet">찾는 항목이 없습니다.</p>
        </section>

        <section v-else-if="step.id === 'review'">
          <div class="budget-wizard__totals">
            <div class="wizard-total wizard-total--in">
              <span>세입</span>
              <strong>{{ formatWon(built.income) }}</strong>
            </div>
            <div class="wizard-total wizard-total--out">
              <span>세출</span>
              <strong>{{ formatWon(built.expense) }}</strong>
            </div>
            <div class="wizard-total" :class="built.income - built.expense < 0 ? 'wizard-total--neg' : 'wizard-total--diff'">
              <span>차액</span>
              <strong>{{ formatWon(built.income - built.expense) }}</strong>
            </div>
          </div>

          <div v-if="labor.base > 0" class="wizard-banner" :class="labor.ok ? 'is-ok' : 'is-short'">
            <div class="wizard-banner__main">
              <span class="wizard-banner__label">인건비 비율</span>
              <strong class="wizard-banner__value">{{ rateText(labor.rate) }}%</strong>
            </div>
            <div class="wizard-banner__sub">
              <span>기준 {{ rateText(labor.required) }}%</span>
              <strong>{{ labor.ok ? "충족" : formatWon(labor.shortMoney) + "원 미달" }}</strong>
            </div>
          </div>

          <p v-if="!built.lines.length" class="budget-wizard__quiet">적은 내용이 없습니다. 표에 적용하면 예산 표의 금액이 비워집니다.</p>
          <div v-else class="wizard-review">
            <div class="wizard-review__col">
              <h3 class="wizard-review__title wizard-review__title--in">세입</h3>
              <ul>
                <li v-for="(line, index) in reviewLines('IN')" :key="'in' + index">
                  <span>{{ line.reviewLabel }}</span>
                  <strong>{{ formatWon(lineAmount(line)) }}</strong>
                </li>
              </ul>
            </div>
            <div class="wizard-review__col">
              <h3 class="wizard-review__title wizard-review__title--out">세출</h3>
              <ul>
                <li v-for="(line, index) in reviewLines('OUT')" :key="'out' + index">
                  <span>{{ line.reviewLabel }}</span>
                  <strong>{{ formatWon(lineAmount(line)) }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      <footer class="budget-wizard__foot">
        <div class="wizard-foot-total" v-if="built.income || built.expense">
          <span class="wizard-foot-total__in">
            <em>세입</em>
            <strong>{{ formatWon(built.income) }}원</strong>
          </span>
          <span class="wizard-foot-total__out">
            <em>세출</em>
            <strong>{{ formatWon(built.expense) }}원</strong>
          </span>
          <span v-if="labor.base > 0" class="wizard-foot-total__labor">
            <em>인건비지출비율</em>
            <strong :class="labor.ok ? 'is-ok' : 'is-short'">{{ rateText(labor.rate) }}%</strong>
            <small>기준 {{ rateText(labor.required) }}%</small>
          </span>
        </div>
        <div class="budget-wizard__foot-bar">
        <div class="budget-wizard__foot-leave">
          <button type="button" class="budget-wizard__btn budget-wizard__btn--quiet" @click="requestClose">닫기</button>
        </div>
        <div class="wizard-foot-note">
          <p v-if="footState || showDraftLink" class="wizard-foot-note__line">
            <span v-if="footState">{{ footState }}</span>
            <button v-if="showDraftLink" type="button" class="wizard-foot-link" @click="requestDraft">임시저장 불러오기</button>
          </p>
        </div>
        <div class="budget-wizard__foot-main">
          <button type="button" class="budget-wizard__btn" :disabled="stepIndex === 0" @click="stepIndex -= 1">
            <v-icon size="15">mdi-chevron-left</v-icon>
            이전
          </button>
          <button v-if="step.id !== 'review'" type="button" class="budget-wizard__btn budget-wizard__btn--next" @click="stepIndex += 1">
            다음
            <v-icon size="15">mdi-chevron-right</v-icon>
          </button>
          <button
            v-else
            type="button"
            class="budget-wizard__btn budget-wizard__btn--next"
            @click="askKind = 'apply'"
          >
            <v-icon size="15">mdi-check</v-icon>
            표에 적용
          </button>
        </div>
        </div>
      </footer>
      <div v-if="askKind" class="wizard-ask" @click.self="askKind = ''">
        <div class="wizard-ask__card" role="dialog" aria-labelledby="wizard-ask-title">
          <h3 id="wizard-ask-title" class="wizard-ask__title">{{ askTitle }}</h3>
          <p class="wizard-ask__lead">{{ askLead }}</p>
          <div v-if="askKind === 'apply' && autoGroups.length" class="wizard-ask__auto">
            <p class="wizard-ask__auto-title">
              같이 넣는 돈
              <span>{{ formatWon(autoKeptTotal) }}원</span>
            </p>
            <div v-for="group in autoGroups" :key="group.id" class="wizard-ask__group">
              <div class="wizard-ask__group-row">
                <label class="wizard-ask__auto-item" :class="{ 'is-off': groupKept(group) === 0 }">
                  <input
                    type="checkbox"
                    :checked="groupKeptCount(group) === group.items.length"
                    :ref="(el) => paintGroupBox(el, group)"
                    @change="toggleGroup(group)"
                  />
                  <span class="wizard-ask__auto-label">{{ group.title }}</span>
                  <strong>{{ formatWon(groupKept(group) || groupTotal(group)) }}원</strong>
                </label>
                <button v-if="group.items.length > 1" type="button" class="wizard-ask__detail" @click="toggleDetail(group.id)">
                  {{ detailOpen[group.id] ? "접기" : "세부사항" }}
                </button>
              </div>
              <div v-if="group.items.length > 1 && detailOpen[group.id]" class="wizard-ask__detail-list">
                <label v-for="item in group.items" :key="item.key" class="wizard-ask__auto-item is-child" :class="{ 'is-off': isAutoOff(item) }">
                  <input type="checkbox" :checked="!isAutoOff(item)" @change="toggleAuto(item)" />
                  <span class="wizard-ask__auto-label">{{ item.label }}</span>
                  <strong>{{ formatWon(item.amount) }}원</strong>
                </label>
              </div>
            </div>
            <p class="wizard-ask__auto-hint">체크를 빼면 그 돈은 표에 적용하지 않습니다. 생계비를 이미 적었다면 그 줄은 빠져 있습니다.</p>
          </div>
          <div class="wizard-ask__actions">
            <button v-if="askKind === 'close'" type="button" class="budget-wizard__btn" @click="discardClose">저장 없이 닫기</button>
            <button type="button" class="budget-wizard__btn" @click="askKind = ''">{{ askKind === "close" ? "계속 작성" : "아니요" }}</button>
            <button type="button" class="budget-wizard__btn budget-wizard__btn--next" @click="confirmAsk">{{ askYes }}</button>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import {
  budgetWizardFamily,
  buildWizardLines,
  clearWizardDraft,
  createGradeAnswers,
  createWizardAnswers,
  dayHourOptions,
  extraGrade,
  familyUsesBasicSplit,
  familyUsesTreatment,
  formatWon,
  formatWonInput,
  gradeLabelOptions,
  hydrateWizardAnswers,
  readWizardDraft,
  writeWizardDraft,
  insuranceGridTotal,
  laborStatus,
  lineAmount,
  MONEY_FIELDS,
  moneyGroups,
  type MoneySheetGwan,
  parseWon,
  preferredDayHours,
  previewPair,
  resolvedGradeRank,
  retirementGridTotal,
  salaryRoleOptions,
  listIncomeMirrors,
  wizardSteps,
  type MoneyField,
  type MoneyMode,
  type WizardAnswers,
  type WizardLine,
  type WizardSheetRow,
  type WizardSuga,
  type WizardTax,
} from "@/utils/budgetWizard";

const props = defineProps<{
  modelValue: boolean;
  bizGubun: string;
  year: string;
  budgetLabel: string;
  sugaList: WizardSuga[];
  tax: WizardTax | null;
  hasExisting: boolean;
  sheetRows: WizardSheetRow[];
  draftKey: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "apply", lines: WizardLine[]): void;
}>();

const family = computed(() => budgetWizardFamily(props.bizGubun));
const steps = computed(() => wizardSteps(family.value));
const incomeGroups = computed(() => moneyGroups("income", family.value));
const spendGroups = computed(() => moneyGroups("spend", family.value));
const stepIndex = ref(0);
const askKind = ref<"" | "apply" | "close" | "sheet" | "draft" | "reset">("");
const excludedAuto = ref<Record<string, boolean>>({});
const viewSource = ref<"sheet" | "draft">("sheet");
const storedDraftAt = ref("");
const baseline = ref("");
const blanked = ref(false);
const autosaveOn = ref(false);
const savedStep = ref(0);
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
const step = computed(() => steps.value[stepIndex.value] || steps.value[0]);
const hourOptions = computed(() => dayHourOptions(props.sugaList));
const usesBasic = computed(() => familyUsesBasicSplit(family.value));

const peopleGridClass = computed(() => {
  if (family.value === "day") return "wizard-grid--day";
  if (family.value === "facility") return "wizard-grid--people-split";
  if (family.value === "care" || family.value === "bath") return "wizard-grid--visit";
  return "wizard-grid--people";
});

function moneyInputCopy(mode: MoneyMode): { a: string; b: string } {
  if (mode === "times") return { a: "1회 금액", b: "횟수" };
  if (mode === "people") return { a: "1명 한 달", b: "인원" };
  if (mode === "span") return { a: "한 달 금액", b: "개월" };
  if (mode === "monthly") return { a: "한 달 금액", b: "" };
  return { a: "1년 금액", b: "" };
}

/** 수입·지출 입력줄의 연간 금액 미리보기 (buildWizardLines와 같은 규칙) */
function annualFieldAmount(field: MoneyField): number {
  const bag = moneyBag(field.id);
  const a = parseWon(bag.a);
  const b = parseWon(bag.b);
  if (a <= 0) return 0;
  if (field.mode === "annual") return a;
  if (field.mode === "monthly") return a * 12;
  if (field.mode === "span") return a * (b > 0 ? b : 12);
  if (field.mode === "times") return b > 0 ? a * b : 0;
  return b > 0 ? a * b * 12 : 0;
}

/** 처우개선비 연간 합계 — (보조 + 본인) × 인원 × 12 */
function treatmentAnnual(): number {
  const count = parseWon(answers.treatmentCount);
  const subsidy = parseWon(answers.treatmentSubsidy);
  const copay = parseWon(answers.treatmentCopay);
  if (count <= 0 || (subsidy <= 0 && copay <= 0)) return 0;
  return (subsidy + copay) * count * 12;
}

/** 표에 넣기에서 고를 수 있는 자동 계산. 사람별 금액은 사회보험·퇴직적립금으로 묶어서 보여 줌 */
interface AutoItem {
  key: string;
  label: string;
  amount: number;
  defaultOff?: boolean;
}

interface AutoGroup {
  id: string;
  title: string;
  items: AutoItem[];
}

const detailOpen = ref<Record<string, boolean>>({});

const autoGroups = computed<AutoGroup[]>(() => {
  const insurance: AutoItem[] = [];
  const retirement: AutoItem[] = [];
  answers.staff.forEach((person) => {
    const preview = staffPreview(person);
    if (!preview) return;
    const role = `${person.role}${person.isCeo ? "(대표자)" : ""}`;
    if (preview.ins > 0) insurance.push({ key: `${person.id}:ins`, label: role, amount: preview.ins });
    if (preview.ret > 0) retirement.push({ key: `${person.id}:ret`, label: role, amount: preview.ret });
  });
  const groups: AutoGroup[] = [];
  if (insurance.length) groups.push({ id: "ins", title: "인건비에서 계산한 사회보험", items: insurance });
  if (retirement.length) groups.push({ id: "ret", title: "인건비에서 계산한 퇴직적립금", items: retirement });
  listIncomeMirrors(family.value, answers).forEach((mirror) => {
    groups.push({
      id: mirror.key,
      title: mirror.label,
      items: [{ key: mirror.key, label: mirror.label, amount: mirror.amount, defaultOff: mirror.defaultOff }],
    });
  });
  return groups;
});

const autoItems = computed(() => autoGroups.value.flatMap((group) => group.items));

function isAutoOff(item: AutoItem): boolean {
  const choice = excludedAuto.value[item.key];
  if (choice === true) return true;
  if (choice === false) return false;
  return Boolean(item.defaultOff);
}

const autoKeptTotal = computed(() => autoItems.value.filter((item) => !isAutoOff(item)).reduce((sum, item) => sum + item.amount, 0));

const excludedAutoKeys = computed(() => autoItems.value.filter((item) => isAutoOff(item)).map((item) => item.key));

function toggleAuto(item: AutoItem) {
  excludedAuto.value = { ...excludedAuto.value, [item.key]: !isAutoOff(item) };
}

function groupKeptCount(group: AutoGroup): number {
  return group.items.filter((item) => !isAutoOff(item)).length;
}

function groupKept(group: AutoGroup): number {
  return group.items.filter((item) => !isAutoOff(item)).reduce((sum, item) => sum + item.amount, 0);
}

function groupTotal(group: AutoGroup): number {
  return group.items.reduce((sum, item) => sum + item.amount, 0);
}

function toggleGroup(group: AutoGroup) {
  const turnOff = groupKeptCount(group) === group.items.length;
  const next = { ...excludedAuto.value };
  group.items.forEach((item) => {
    next[item.key] = turnOff;
  });
  excludedAuto.value = next;
}

function toggleDetail(id: string) {
  detailOpen.value = { ...detailOpen.value, [id]: !detailOpen.value[id] };
}

function paintGroupBox(el: unknown, group: AutoGroup) {
  if (!(el instanceof HTMLInputElement)) return;
  const kept = groupKeptCount(group);
  el.indeterminate = kept > 0 && kept < group.items.length;
}

function goTo(index: number) {
  if (index < 0 || index >= steps.value.length) return;
  stepIndex.value = index;
}

const moneyQuery = ref("");

const visibleMoneyGroups = computed((): MoneySheetGwan[] => {
  const source = step.value.id === "income" ? incomeGroups.value : spendGroups.value;
  const query = moneyQuery.value.trim();
  return source
    .map((gwan) => ({
      gwan: gwan.gwan,
      hangs: gwan.hangs
        .map((hang) => ({
          hang: hang.hang,
          showHang: hang.showHang,
          groups: hang.groups
            .map((group) => ({
              title: group.title,
              fields: group.fields.filter((field) => !query || `${field.label} ${field.hint} ${group.title} ${hang.hang} ${gwan.gwan}`.includes(query)),
            }))
            .filter((group) => group.fields.length > 0),
        }))
        .filter((hang) => hang.groups.length > 0),
    }))
    .filter((gwan) => gwan.hangs.length > 0);
});

function focusNextField(event: KeyboardEvent) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
  if (target instanceof HTMLInputElement && target.type === "checkbox") return;
  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;
  const fields = Array.from(root.querySelectorAll("input, select")).filter((node) => {
    if (!(node instanceof HTMLInputElement) && !(node instanceof HTMLSelectElement)) return false;
    if (node.disabled) return false;
    if (node instanceof HTMLInputElement && node.type === "checkbox") return false;
    return node.getClientRects().length > 0;
  });
  const next = fields[fields.indexOf(target) + 1];
  if (!(next instanceof HTMLElement)) return;
  event.preventDefault();
  next.focus();
}

const answers = reactive<WizardAnswers>(createWizardAnswers(props.bizGubun, props.sugaList));

function fillAnswers(next: WizardAnswers) {
  answers.dayHours = next.dayHours;
  answers.grades.splice(0, answers.grades.length, ...next.grades);
  answers.staff.splice(0, answers.staff.length, ...next.staff);
  answers.treatmentCount = next.treatmentCount;
  answers.treatmentSubsidy = next.treatmentSubsidy;
  answers.treatmentCopay = next.treatmentCopay;
  Object.keys(answers.money).forEach((key) => delete answers.money[key]);
  Object.assign(answers.money, next.money);
  MONEY_FIELDS.forEach((field) => {
    if (!answers.money[field.id]) answers.money[field.id] = { a: "", b: field.mode === "span" ? "12" : "" };
  });
}

function snapshotAnswers(): string {
  return JSON.stringify(answers);
}

function markBaseline() {
  baseline.value = snapshotAnswers();
}

function stopAutosaveTimer() {
  if (!autosaveTimer) return;
  clearTimeout(autosaveTimer);
  autosaveTimer = null;
}

function loadSheet() {
  fillAnswers(hydrateWizardAnswers(props.bizGubun, props.sugaList, props.sheetRows || []));
  stepIndex.value = 0;
  viewSource.value = "sheet";
  blanked.value = false;
  savedStep.value = 0;
  markBaseline();
}

function loadDraft(step: number, next: WizardAnswers) {
  fillAnswers(next);
  const last = Math.max(0, steps.value.length - 1);
  stepIndex.value = Math.min(Math.max(0, step), last);
  viewSource.value = "draft";
  blanked.value = false;
  savedStep.value = stepIndex.value;
  markBaseline();
}

function openFromStorage() {
  autosaveOn.value = false;
  stopAutosaveTimer();
  askKind.value = "";
  excludedAuto.value = {};
  detailOpen.value = {};
  blanked.value = false;
  const draft = readWizardDraft(props.draftKey);
  storedDraftAt.value = draft?.savedAt || "";
  if (draft) loadDraft(draft.stepIndex, draft.answers);
  else loadSheet();
  autosaveOn.value = true;
}

function saveDraft(closeAfter = false) {
  stopAutosaveTimer();
  if (!props.draftKey) {
    if (closeAfter) emit("update:modelValue", false);
    return;
  }
  const savedAt = new Date().toISOString();
  writeWizardDraft(props.draftKey, {
    savedAt,
    stepIndex: stepIndex.value,
    answers: JSON.parse(JSON.stringify(answers)) as WizardAnswers,
  });
  storedDraftAt.value = savedAt;
  viewSource.value = "draft";
  blanked.value = false;
  savedStep.value = stepIndex.value;
  markBaseline();
  if (closeAfter) emit("update:modelValue", false);
}

function scheduleAutosave() {
  if (!autosaveOn.value || !props.modelValue) return;
  if (!dirty.value && stepIndex.value === savedStep.value) return;
  stopAutosaveTimer();
  autosaveTimer = setTimeout(() => {
    autosaveTimer = null;
    if (!autosaveOn.value || !props.modelValue) return;
    if (!dirty.value && stepIndex.value === savedStep.value) return;
    saveDraft();
  }, 500);
}

function applyReset() {
  autosaveOn.value = false;
  stopAutosaveTimer();
  clearWizardDraft(props.draftKey);
  storedDraftAt.value = "";
  fillAnswers(createWizardAnswers(props.bizGubun, props.sugaList));
  stepIndex.value = 0;
  viewSource.value = "sheet";
  blanked.value = true;
  savedStep.value = 0;
  markBaseline();
  autosaveOn.value = true;
}

function discardClose() {
  askKind.value = "";
  emit("update:modelValue", false);
}

function requestClose() {
  if (dirty.value || stepIndex.value !== savedStep.value) saveDraft();
  emit("update:modelValue", false);
}

function requestSheet() {
  if (viewSource.value === "sheet" && !dirty.value && !blanked.value) return;
  if (dirty.value) askKind.value = "sheet";
  else loadSheet();
}

function requestDraft() {
  if (!storedDraftAt.value) return;
  if (viewSource.value === "draft" && !dirty.value) return;
  if (dirty.value) askKind.value = "draft";
  else {
    const draft = readWizardDraft(props.draftKey);
    if (draft) loadDraft(draft.stepIndex, draft.answers);
  }
}

function confirmAsk() {
  const kind = askKind.value;
  askKind.value = "";
  if (kind === "apply") {
    if (dirty.value || viewSource.value === "draft") saveDraft();
    emit("apply", built.value.lines);
    return;
  }
  if (kind === "close") {
    saveDraft(true);
    return;
  }
  if (kind === "sheet") {
    loadSheet();
    return;
  }
  if (kind === "draft") {
    const draft = readWizardDraft(props.draftKey);
    if (draft) loadDraft(draft.stepIndex, draft.answers);
    return;
  }
  if (kind === "reset") applyReset();
}

const dirty = computed(() => baseline.value !== "" && snapshotAnswers() !== baseline.value);

const sourceTime = computed(() => {
  if (!storedDraftAt.value) return "";
  const date = new Date(storedDraftAt.value);
  if (Number.isNaN(date.getTime())) return "";
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${date.getMonth() + 1}월 ${date.getDate()}일 ${hour}:${minute}`;
});

const footState = computed(() => {
  if (dirty.value || stepIndex.value !== savedStep.value) return "임시저장 중";
  if (blanked.value) return "초기화됨";
  if (viewSource.value === "draft" && sourceTime.value) return `자동 임시저장됨 ${sourceTime.value}`;
  return "";
});

const showSheetLink = computed(() => viewSource.value === "draft" || dirty.value || blanked.value);
const showDraftLink = computed(() => Boolean(storedDraftAt.value) && !(viewSource.value === "draft" && !dirty.value));

const askTitle = computed(() => {
  if (askKind.value === "close") return "임시저장 후 닫기";
  if (askKind.value === "sheet") return "표로 되돌리기";
  if (askKind.value === "draft") return "임시저장으로 바꾸기";
  if (askKind.value === "reset") return "모두 지우기";
  return "표에 적용";
});

const askLead = computed(() => {
  if (askKind.value === "close") return "임시저장하지 않은 수정이 있습니다. 저장한 뒤 닫으면 다음에 이어서 볼 수 있습니다.";
  if (askKind.value === "sheet") return "마법사에서 고친 내용은 버리고, 예산 표에 적힌 값으로 다시 엽니다. 임시저장은 그대로 둡니다.";
  if (askKind.value === "draft") return "화면의 수정을 버리고 마지막 임시저장본을 다시 엽니다.";
  if (askKind.value === "reset") return "마법사에 적은 내용과 임시저장을 지우고 빈 화면으로 돌아갑니다. 예산 표는 바꾸지 않습니다.";
  if (!built.value.lines.length) return "적은 내용이 없습니다. 예산 표의 금액을 비웁니다. 서버에는 아직 저장되지 않습니다. 남기려면 예산 화면에서 저장을 누르세요.";
  if (viewSource.value === "draft") return "임시저장해 둔 내용으로 화면의 예산 표를 바꿉니다. 서버에는 아직 저장되지 않습니다. 남기려면 예산 화면에서 저장을 누르세요.";
  return "지금 적은 내용으로 화면의 예산 표를 바꿉니다. 서버에는 아직 저장되지 않습니다. 남기려면 예산 화면에서 저장을 누르세요.";
});

const askYes = computed(() => {
  if (askKind.value === "close") return "임시저장 후 닫기";
  if (askKind.value === "sheet") return "되돌리기";
  if (askKind.value === "draft") return "불러오기";
  if (askKind.value === "reset") return "지우기";
  return "표에 적용";
});

watch(stepIndex, () => {
  moneyQuery.value = "";
});

watch(
  () => (props.modelValue ? `${stepIndex.value}\n${snapshotAnswers()}` : ""),
  () => {
    scheduleAutosave();
  }
);

watch(
  () => props.modelValue,
  (open) => {
    if (!open) {
      autosaveOn.value = false;
      stopAutosaveTimer();
      askKind.value = "";
      return;
    }
    openFromStorage();
  }
);

onBeforeUnmount(() => {
  stopAutosaveTimer();
});

watch(
  () => (props.sugaList || []).map((row) => row.suga_rank).join("|"),
  () => {
    const list = props.sugaList || [];
    if (!list.length) return;
    const wasDirty = dirty.value;
    const freshGrades = createGradeAnswers(props.bizGubun, list);
    if (!answers.dayHours) answers.dayHours = preferredDayHours(list);
    if (!answers.grades.length) {
      answers.grades.push(...freshGrades);
      return;
    }
    freshGrades.forEach((grade, index) => {
      const current = answers.grades[index];
      if (!current) return;
      if (!current.rank) current.rank = grade.rank;
      if (!current.times) current.times = grade.times;
      if (!current.hours && grade.hours) current.hours = grade.hours;
    });
    if (!wasDirty) markBaseline();
  }
);

const built = computed(() => buildWizardLines(props.bizGubun, answers, props.sugaList, props.tax, excludedAutoKeys.value));
const labor = computed(() => laborStatus(built.value.lines, props.tax?.tr_labor_cost_rate));

function rateText(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function reviewLines(trcode: "IN" | "OUT") {
  return built.value.lines.filter((line) => line.trcode === trcode);
}

function moneyBag(id: string) {
  if (!answers.money[id]) answers.money[id] = { a: "", b: "" };
  return answers.money[id];
}

function onDigits(target: { [key: string]: string }, key: string, event: Event) {
  const value = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, "");
  target[key] = value;
}

function onWon(target: { [key: string]: string }, key: string, event: Event) {
  target[key] = formatWonInput((event.target as HTMLInputElement).value);
}

function onAnswerDigits(key: "treatmentCount", event: Event) {
  answers[key] = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, "");
}

function onAnswerWon(key: "treatmentSubsidy" | "treatmentCopay", event: Event) {
  answers[key] = formatWonInput((event.target as HTMLInputElement).value);
}

function onMoney(id: string, key: "a" | "b", event: Event, won = true) {
  const bag = moneyBag(id);
  const raw = (event.target as HTMLInputElement).value;
  bag[key] = won && key === "a" ? formatWonInput(raw) : raw.replace(/[^0-9]/g, "");
  if (key === "b") bag[key] = raw.replace(/[^0-9]/g, "");
}

function addGrade() {
  answers.grades.push(extraGrade(props.bizGubun, props.sugaList, answers.dayHours, answers.grades.length));
}

function removeGrade(index: number) {
  if (answers.grades.length <= 1) return;
  answers.grades.splice(index, 1);
}

function onStaffRole(person: WizardAnswers["staff"][number]) {
  if (person.role !== "센터장") person.isCeo = false;
}

function addStaff(kind: "direct" | "indirect") {
  answers.staff.push({
    id: `s${Date.now()}`,
    role: kind === "indirect" ? "센터장" : "사회복지사",
    kind,
    isCeo: false,
    count: "",
    monthly: "",
  });
}

function removeStaff(index: number) {
  answers.staff.splice(index, 1);
}

function pairPreview(grade: WizardAnswers["grades"][number]) {
  const rank = resolvedGradeRank(family.value, grade, answers.dayHours);
  const suga = props.sugaList.find((row) => row.suga_rank === rank);
  const times = parseWon(grade.times);
  const count = parseWon(grade.count);
  const basicCount = parseWon(grade.basicCount);
  if (!suga || times <= 0 || (count <= 0 && basicCount <= 0)) return null;
  const normal = previewPair(suga, times, false);
  const basic = previewPair(suga, times, true);
  return {
    bonin: normal.bonin * count * 12,
    benefit: normal.benefit * count * 12,
    basicBenefit: basic.benefit * basicCount * 12,
    showNormal: count > 0,
    showBasic: familyUsesBasicSplit(family.value) && basicCount > 0,
  };
}

function staffPreview(person: WizardAnswers["staff"][number]) {
  const monthly = parseWon(person.monthly);
  const count = parseWon(person.count);
  if (monthly <= 0 || count <= 0) return null;
  const pay = monthly * count * 12;
  const ins = insuranceGridTotal(monthly, count, 12, person.isCeo, props.tax);
  const ret = retirementGridTotal(monthly, count, 12, person.isCeo);
  return { pay, ins, ret, annual: pay + ins + ret };
}


</script>
